import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { COLORS } from '../constants/booking';

const Breadcrumb: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  // Map routes to display names
  const routeMap: Record<string, string> = {
    '/dashboard': 'Dashboard',
    '/dashboard/tenants': 'Tenants',
    '/dashboard/tenant-settings': 'Tenant Settings',
    '/dashboard/roles': 'Roles',
    '/dashboard/permissions': 'Permissions',
    '/dashboard/users': 'Staff',
    '/dashboard/staff-categories': 'Staff Categories',
    '/dashboard/patients': 'Patients',
    '/dashboard/ambulances': 'Ambulances',
    '/dashboard/ambulance-types': 'Ambulance Types',
    '/dashboard/bookings': 'Bookings',
    '/dashboard/gps-devices': 'GPS Devices',
    '/dashboard/reports': 'Reports',
    '/dashboard/profile': 'Profile',
    '/dashboard/attendance': 'Attendance',
    '/dashboard/patient-vital-fields': 'Patient Vital Fields',
    '/dashboard/areas': 'Areas',
    '/dashboard/fuel': 'Fuel',
    '/dashboard/settings': 'Settings',
  };

  // Get breadcrumb items from path
  const getBreadcrumbs = () => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const breadcrumbs: Array<{ path: string; label: string }> = [];

    // Always start with Dashboard
    breadcrumbs.push({ path: '/dashboard', label: 'Dashboard' });

    // Build path incrementally, but skip 'dashboard' segment to avoid duplication
    let currentPath = '';
    pathSegments.forEach((segment, index) => {
      // Skip the 'dashboard' segment since we already added it
      if (segment === 'dashboard' && index === 0) {
        currentPath = '/dashboard';
        return;
      }
      
      currentPath += `/${segment}`;
      const label = routeMap[currentPath] || segment
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
      
      breadcrumbs.push({ path: currentPath, label });
    });

    // Handle query params for tabs
    const searchParams = new URLSearchParams(location.search);
    const tab = searchParams.get('tab');
    const mainTab = searchParams.get('mainTab');
    const subTab = searchParams.get('subTab');
    
    let tabLabel: string | null = null;
    let mainTabLabel: string | null = null;
    let subTabLabel: string | null = null;
    
    // Handle Settings page tabs (mainTab and subTab)
    if (location.pathname === '/dashboard/settings') {
      const mainTabLabels: Record<string, string> = {
        'ambulance': 'Ambulance',
        'staff': 'Staff',
        'patient': 'Patient',
        'tenant': 'Tenant',
        'booking': 'Booking',
      };
      
      const subTabLabels: Record<string, string> = {
        // Ambulance sub tabs
        'ambulance-table': 'Ambulances',
        'ambulance-type': 'Ambulance Type',
        'gps-devices': 'GPS Devices',
        'areas': 'Areas',
        // Staff sub tabs
        'staff-categories': 'Staff Categories',
        'roles': 'Roles',
        // Patient sub tabs
        'patient-vital-fields': 'Patient Vital Fields',
        // Tenant sub tabs
        'tenant-settings': 'Tenant Settings',
        'permissions': 'Permissions',
        'profile': 'Profile',
        'login-page-config': 'Login Page Config',
        // Booking sub tabs
        'cancel-reason': 'Cancel Reasons',
      };
      
      if (mainTab && mainTabLabels[mainTab]) {
        mainTabLabel = mainTabLabels[mainTab];
      }
      if (subTab && subTabLabels[subTab]) {
        subTabLabel = subTabLabels[subTab];
      }
    } else if (tab) {
      // Handle other pages' tab query params
      const tabLabels: Record<string, string> = {
        // Ambulance tabs
        'areaMapping': 'Area Mapping',
        'mapping': 'Staff Mapping',
        'history': 'Assignment History',
        // Report tabs
        'ambulances': 'Ambulances on Duty',
        'staff': 'Staff on Duty',
        'admitted': 'Admitted Patients',
        'incoming': 'Incoming Patients',
        'waiting': 'Waiting Patients',
        'attendance': 'Attendance',
        'vehicle': 'Vehicle Report',
        'trips': 'Trips',
      };
      if (tabLabels[tab]) {
        tabLabel = tabLabels[tab];
      }
    }

    return { breadcrumbs, tabLabel, mainTabLabel, subTabLabel };
  };

  const { breadcrumbs, tabLabel, mainTabLabel, subTabLabel } = getBreadcrumbs();
  
  // Get mainTab from URL for navigation
  const urlSearchParams = new URLSearchParams(location.search);
  const currentMainTab = urlSearchParams.get('mainTab');
  
  // Determine if we're on Settings page with tabs
  const isSettingsPage = location.pathname === '/dashboard/settings';
  const hasSettingsTabs = isSettingsPage && (mainTabLabel || subTabLabel);
  const isLastBreadcrumb = (index: number) => {
    if (hasSettingsTabs) {
      return index === breadcrumbs.length - 1 && !mainTabLabel && !subTabLabel;
    }
    return index === breadcrumbs.length - 1 && !tabLabel;
  };

  return (
    <nav
      style={{
        padding: '0',
        backgroundColor: 'transparent',
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        fontSize: '14px',
        flexWrap: 'nowrap',
        whiteSpace: 'nowrap',
        minHeight: '20px',
        overflow: 'hidden',
      }}
    >
      {breadcrumbs.map((crumb, index) => {
        const isLast = isLastBreadcrumb(index);
        return (
          <React.Fragment key={crumb.path}>
            {index > 0 && (
              <span
                style={{
                  color: isDark ? COLORS.THEME.DARK.TEXT_SECONDARY : COLORS.THEME.LIGHT.TEXT_SECONDARY,
                  flexShrink: 0,
                }}
              >
                /
              </span>
            )}
            {isLast ? (
              <span
                style={{
                  color: isDark ? COLORS.THEME.DARK.TEXT_PRIMARY : COLORS.THEME.LIGHT.TEXT_PRIMARY,
                  fontWeight: '600',
                  flexShrink: 0,
                }}
              >
                {crumb.label}
              </span>
            ) : (
              <button
                onClick={() => {
                  // Clear query params when navigating to parent (resets to first tab)
                  navigate(crumb.path, { replace: true });
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: isDark ? '#60a5fa' : '#2563eb',
                  cursor: 'pointer',
                  padding: '0',
                  borderRadius: '4px',
                  fontSize: '14px',
                  transition: 'background-color 0.2s',
                  textDecoration: 'none',
                  flexShrink: 0,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.textDecoration = 'underline';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.textDecoration = 'none';
                }}
              >
                {crumb.label}
              </button>
            )}
          </React.Fragment>
        );
      })}
      {/* Settings page tabs */}
      {mainTabLabel && (
        <>
          <span
            style={{
              color: isDark ? COLORS.THEME.DARK.TEXT_SECONDARY : COLORS.THEME.LIGHT.TEXT_SECONDARY,
              margin: '0 4px',
            }}
          >
            {'>'}
          </span>
          <button
            onClick={() => {
              // Navigate to Settings with mainTab but clear subTab
              if (currentMainTab) {
                navigate(`/dashboard/settings?mainTab=${currentMainTab}`, { replace: true });
              }
            }}
            style={{
              background: 'none',
              border: 'none',
              color: subTabLabel 
                ? (isDark ? '#60a5fa' : '#2563eb')
                : (isDark ? COLORS.THEME.DARK.TEXT_PRIMARY : COLORS.THEME.LIGHT.TEXT_PRIMARY),
              cursor: subTabLabel ? 'pointer' : 'default',
              padding: '4px 8px',
              borderRadius: '4px',
              fontSize: '16px',
              fontWeight: subTabLabel ? '400' : '600',
              transition: 'background-color 0.2s',
              textDecoration: 'none',
            }}
            onMouseEnter={(e) => {
              if (subTabLabel) {
                e.currentTarget.style.backgroundColor = isDark ? 'rgba(96, 165, 250, 0.1)' : 'rgba(37, 99, 235, 0.1)';
                e.currentTarget.style.textDecoration = 'underline';
              }
            }}
            onMouseLeave={(e) => {
              if (subTabLabel) {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.textDecoration = 'none';
              }
            }}
          >
            {mainTabLabel}
          </button>
        </>
      )}
      {subTabLabel && (
        <>
          <span
            style={{
              color: isDark ? COLORS.THEME.DARK.TEXT_SECONDARY : COLORS.THEME.LIGHT.TEXT_SECONDARY,
              margin: '0 4px',
            }}
          >
            {'>'}
          </span>
          <span
            style={{
              color: isDark ? COLORS.THEME.DARK.TEXT_PRIMARY : COLORS.THEME.LIGHT.TEXT_PRIMARY,
              fontWeight: '600',
            }}
          >
            {subTabLabel}
          </span>
        </>
      )}
      {/* Other pages' tab labels */}
      {tabLabel && (
        <>
          <span
            style={{
              color: isDark ? COLORS.THEME.DARK.TEXT_SECONDARY : COLORS.THEME.LIGHT.TEXT_SECONDARY,
              margin: '0 4px',
            }}
          >
            {'>'}
          </span>
          <span
            style={{
              color: isDark ? COLORS.THEME.DARK.TEXT_PRIMARY : COLORS.THEME.LIGHT.TEXT_PRIMARY,
              fontWeight: '600',
            }}
          >
            {tabLabel}
          </span>
        </>
      )}
    </nav>
  );
};

export default Breadcrumb;

